// src/features/executive/performance/drill/AppointmentsDrill.tsx
import { Box, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useAppointmentDetail } from '../hooks/use-appointment-detail'
import DrillTable from 'features/executive/components/DrillTable/DrillTable'
import type { ExecutiveState } from 'app/slice/executive.slice'
import { useRegisterDrillExport } from 'features/executive/components/DrillDrawer/DrillExportContext'
import { formatDateThaiShort } from 'utils/executive.utils'

// Status keys in the same order the rest of the system shows them
// (appointment list / history screens). Statuses with zero across every
// row are hidden so the drill table stays narrow when only a few states
// are in use.
const STATUS_ORDER = [
  'NORMAL', 'CONFIRM', 'PUT_OFF', 'CAN_NOT_CONTACT', 'NOT_YET', 'ARRIVED',
  'PENDING_SERVICE', 'SERVICE', 'PENDING_PAYMENT', 'PAYMENT',
  'DONE', 'CANCEL', 'CANCEL_APP', 'WAITING_APPROVE',
]

export default function AppointmentsDrill() {
  const { t } = useTranslation()
  const stack = useSelector((s: { executive: ExecutiveState }) => s.executive.drillStack)
  const top = stack[stack.length - 1]
  const { data, loading, error, refetch } = useAppointmentDetail(top?.scopedFilters)

  const widgetTitle = top?.label ?? t('EXECUTIVE.PERFORMANCE.APPOINTMENTS.TITLE')
  useRegisterDrillExport(
    data ? { drillId: 'appointments', data: data.rows } : null,
    'performance.appointment-detail',
    widgetTitle
  )

  const visibleStatuses = useMemo(() => {
    const rows = data?.rows ?? []
    if (rows.length === 0) return [] as string[]
    return STATUS_ORDER.filter((s) => rows.some((r) => (r.byStatus?.[s] ?? 0) > 0))
  }, [data])

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {top?.label ?? t('EXECUTIVE.PERFORMANCE.APPOINTMENTS.TITLE')}
      </Typography>
      <DrillTable
        rows={data?.rows ?? []}
        columns={[
          { key: 'date', label: t('EXECUTIVE.DRILL.DATE', 'Date'), render: (r) => formatDateThaiShort(r.date) },
          { key: 'total', label: t('EXECUTIVE.PERFORMANCE.APPOINTMENTS.TOTAL'), align: 'right', render: (r) => r.total },
          ...visibleStatuses.map((status) => ({
            key: status,
            label: t(`APPOINTMENT.STATUS.${status}`, status),
            align: 'right' as const,
            render: (r: any) => r.byStatus?.[status] ?? 0,
          })),
        ]}
        loading={loading}
        error={error}
        onRetry={refetch}
      />
    </Box>
  )
}
